import { Document as RichTextDocument } from '@contentful/rich-text-types';
import { useTheme } from '@mui/material';
import { useRollbar } from '@rollbar/react';
import classNames from 'classnames';
import { useState } from 'react';

import {
  BatteryLeadFormData,
  submitBatteryLeadToGoogle,
} from '@/clients/googleSheetClient';
import FormInput, {
  InputRow,
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import FormResultDialog, {
  DialogContent,
} from '@/components/FormResultDialog/FormResultDialog';
import HomeOwnershipRadioContainer from '@/components/HomeOwnershipRadioContainer/HomeOwnershipRadioContainer';
import Button, { ButtonVariant } from '@/components/OldButton/OldButton';
import RichText from '@/components/RichText/RichText';
import { GoogleUrls } from '@/constants/baseUrls';
import { LeadFormType } from '@/enums/FormType';
import {
  LeadFormInputData,
  OwnHome,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import {
  EMAIL_REGEX,
  PHONE_REGEX,
  ZIP_REGEX,
} from '@/utils/validation/constants';

import useStyles from './LeadFormV2.styles';

export interface LeadFormV2Props {
  campaignId: string;
  tos: RichTextDocument;
  dialogContent: DialogContent;
  leadFormType: LeadFormType;
  googleSheetUrls?: GoogleUrls;
}

/**
 * @deprecated use /ActionUserInput/LeadForm instead
 */
export default function LeadFormV2({
  campaignId: contentfulCampaignId,
  tos,
  dialogContent,
  leadFormType,
  googleSheetUrls,
}: LeadFormV2Props) {
  const [hasError, setHasError] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [homeOwnership, setHomeOwnership] = useState<OwnHome>(OwnHome.OWN);
  const [networkError, setNetworkError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLeadFormSubmitted, setIsLeadFormSubmitted] =
    useState<boolean>(false);
  const [showLeadModal, setShowLeadModal] = useState<boolean>(false);
  const rollbar = useRollbar();
  const { submitLeadToZeus } = useSubmitLeadToZeus();
  const { typography } = useTheme();

  function validateInputs(): boolean {
    if (!firstName || !lastName || !email || !zip || !phone || !homeOwnership) {
      setHasError(true);
      return false;
    } else if (
      (email && !email.match(EMAIL_REGEX)) ||
      (phone && !phone.match(PHONE_REGEX)) ||
      (zip && !zip.match(ZIP_REGEX))
    ) {
      setHasError(true);
      return false;
    } else {
      setHasError(false);
      return true;
    }
  }

  function getError(value?: string, pattern?: RegExp): string {
    if (!hasError) return '';

    if (!value) return 'This is a required field';
    if (pattern && !value.match(pattern)) {
      return `Incorrect format`;
    }
    return '';
  }

  const handleSubmit = async () => {
    if (validateInputs()) {
      try {
        setIsSubmitting(true);

        if (leadFormType === LeadFormType.ZEUS) {
          const zeusLeadFormData: LeadFormInputData = {
            first_name: firstName,
            last_name: lastName,
            email,
            zip,
            phone_home: phone,
            property_ownership: homeOwnership,
          };
          await submitLeadToZeus({
            zeusData: zeusLeadFormData,
            contentfulCampaignId,
            gtmSubmitEventFormType: 'Standard Lead Form',
            honeypot: Boolean(address),
          });
        }
        if (leadFormType === LeadFormType.BATTERY && googleSheetUrls) {
          const batteryLeadFormData: BatteryLeadFormData = {
            firstName,
            lastName,
            email,
            phone,
            zip,
          };
          await submitBatteryLeadToGoogle({
            batteryData: batteryLeadFormData,
            googleSheetUrls,
            propertyOwnership: homeOwnership,
            honeypot: Boolean(address),
          });
        }
        setNetworkError('');
        setShowLeadModal(true);
        setIsLeadFormSubmitted(true);
      } catch (err: unknown) {
        rollbar.error(getErrorMessage(err), getErrorObject(err));
        setNetworkError(
          'There was an error submitting the form, please try again'
        );
      }
      setIsSubmitting(false);
    }
  };

  const { classes } = useStyles();

  return (
    <form className={classNames([classes.leadForm, 'lead-form'])}>
      <FormResultDialog
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        dialogContent={dialogContent}
      />
      <InputRow>
        <FormInput
          name='firstNameInput'
          label='First Name'
          value={firstName}
          setValue={setFirstName}
          error={getError(firstName)}
        />
      </InputRow>
      <InputRow>
        <FormInput
          name='lastNameInput'
          label='Last Name'
          value={lastName}
          setValue={setLastName}
          error={getError(lastName)}
        />
      </InputRow>
      <InputRow>
        <FormInput
          name='emailInput'
          label='Email'
          value={email}
          setValue={setEmail}
          error={getError(email, EMAIL_REGEX)}
          inputMode='email'
        />
      </InputRow>
      <InputRow classes={classes.hidden}>
        <FormInput
          name='addressInput'
          label='Address'
          value={address}
          setValue={setAddress}
          inputMode='text'
          autoComplete='off'
        />
      </InputRow>
      <InputRow classes={classes.splitRow}>
        <FormInput
          name='zipCodeInput'
          label='Zip code'
          value={zip}
          setValue={setZip}
          error={getError(zip, ZIP_REGEX)}
          type={InputType.NUMBER}
          inputMode='numeric'
        />
        <FormInput
          name='phoneInput'
          label='Phone'
          value={phone}
          setValue={setPhone}
          error={getError(phone, PHONE_REGEX)}
          type={InputType.NUMBER}
          inputMode='tel'
        />
      </InputRow>
      <div>
        <HomeOwnershipRadioContainer
          homeOwnership={homeOwnership}
          onHomeOwnershipChange={setHomeOwnership}
        />
        <div className={classes.tosText}>
          <label>
            <input
              type='hidden'
              id='leadid_tcpa_disclosure'
              name='Terms of Service'
            />
            <RichText content={tos} className={classes.paragraphOverride} />
          </label>
        </div>
      </div>
      <div style={{ width: '100%' }}>
        <Button
          variant={ButtonVariant.PrimaryBluePill}
          onClick={handleSubmit}
          label='Start your quote'
          disabled={isLeadFormSubmitted || isSubmitting}
        />
        <p
          className={classes.errorText}
          style={{ marginTop: typography.pxToRem(10) }}
        >
          {networkError}
        </p>
      </div>
    </form>
  );
}
